/**
 * The Budget screen (f10.10, adr-10) -- Administration, not a preparation step.
 *
 * Finance-maintained reference data: an annual figure per client and twelve
 * monthly figures per Business Unit, each with an explicit currency, plus a
 * CSV import with a staged preview. The review screen (f12.1) reads these
 * figures; nothing here builds a budgeting process.
 *
 * Visibility comes from the server's navigation list, and hiding the screen is
 * presentation only: every `/budgets` route -- reads included -- answers 403 to
 * anyone but Finance and Admin, and records the refusal.
 */

import { useState, type ReactNode } from 'react';
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Landmark,
  ShieldAlert,
  UploadCloud,
  type LucideIcon,
} from 'lucide-react';

import { useSessionScope } from '@/session/SessionScopeContext';
import '@/styles/budget.css';

import { BudgetImportsPanel } from './BudgetImportsPanel';
import { ClientAnnualPanel } from './ClientAnnualPanel';
import { UnitMonthlyPanel } from './UnitMonthlyPanel';
import { yearOptions } from './budgetView';

const NAV_KEY = 'budget';

type TabId = 'client-annual' | 'unit-monthly' | 'imports';

const TABS: readonly { id: TabId; label: string; icon: LucideIcon }[] = [
  { id: 'client-annual', label: 'Client annual', icon: Landmark },
  { id: 'unit-monthly', label: 'Unit monthly', icon: CalendarRange },
  { id: 'imports', label: 'Imports', icon: UploadCloud },
];

function PageHead({ children }: { children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Budget</h1>
        <div className="page-sub">Finance reference data · Finance and Admin only</div>
      </div>
      <div className="page-actions">{children}</div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <div className="card">
        <div className="card-body">
          <div className="empty">
            <div className="e-icon e-icon-warning">
              <ShieldAlert aria-hidden="true" />
            </div>
            <h3>Finance and Admin only</h3>
            <p>
              Budget figures are maintained by Finance. The server refuses anyone else, and records the refusal
              in the audit log.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function YearStepper({ year, onChange }: { year: number; onChange: (year: number) => void }) {
  const options = yearOptions(new Date().getFullYear());
  const first = options[0] ?? year;
  const last = options[options.length - 1] ?? year;
  return (
    <div className="budget-year" role="group" aria-label="Budget year">
      <button type="button" className="btn btn-secondary btn-sm budget-year-step" aria-label="Previous year"
        disabled={year <= first} onClick={() => onChange(year - 1)}>
        <ChevronLeft aria-hidden="true" />
      </button>
      <select aria-label="Year" value={year} onChange={(event) => onChange(Number(event.target.value))}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <button type="button" className="btn btn-secondary btn-sm budget-year-step" aria-label="Next year"
        disabled={year >= last} onClick={() => onChange(year + 1)}>
        <ChevronRight aria-hidden="true" />
      </button>
    </div>
  );
}

export function BudgetPage() {
  const { canSee } = useSessionScope();
  if (!canSee(NAV_KEY)) return <NoAccess />;
  return <BudgetScreen />;
}

export function BudgetScreen() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [tab, setTab] = useState<TabId>('client-annual');

  return (
    <>
      <PageHead>
        {tab !== 'imports' && <YearStepper year={year} onChange={setYear} />}
      </PageHead>

      <div className="tabs" role="tablist" aria-label="Budget tables">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`budget-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`budget-panel-${id}`}
            className={`tab${tab === id ? ' is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            <Icon aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`budget-panel-${tab}`} aria-labelledby={`budget-tab-${tab}`} className="budget-panel">
        {tab === 'client-annual' && <ClientAnnualPanel year={year} />}
        {tab === 'unit-monthly' && <UnitMonthlyPanel year={year} />}
        {tab === 'imports' && <BudgetImportsPanel />}
      </div>
    </>
  );
}
